import { useEffect, useRef, useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { adminSession } from '@/lib/api';
import { api } from '../../../convex/functions/_generated/api';
import type { Id } from '../../../convex/functions/_generated/dataModel';
import { friendlyError } from '@/lib/errors';

/**
 * Support inbox — threads on the left, conversation on the right. Backed by the
 * app-owned supportThreads / supportMessages tables (Cloudflare Email migration):
 * inbound mail to the support address + in-app contact-form submissions land here;
 * replies go back out as threaded email from support@wydm.app.
 */

type Status = 'open' | 'pending' | 'closed';
// 'spam' is a separate tab, not a status: the other filters all EXCLUDE
// spam-flagged threads (listThreads default), keeping the main inbox clean.
type Filter = 'all' | Status | 'spam';
const FILTERS: Filter[] = ['all', 'open', 'pending', 'closed', 'spam'];

const STATUS_COLOR: Record<Status, string> = {
  open: 'var(--coral)',
  pending: '#B8860B',
  closed: 'var(--ink-3)',
};

export function SupportPage() {
  const token = adminSession.getToken() ?? '';
  const [filter, setFilter] = useState<Filter>('open');
  const [selected, setSelected] = useState<Id<'supportThreads'> | null>(null);

  const threads = useQuery(
    api.admin.support.listThreads,
    token
      ? {
          token,
          ...(filter === 'spam'
            ? { spam: true }
            : filter === 'all'
              ? {}
              : { status: filter }),
        }
      : 'skip',
  );
  const loading = threads === undefined;
  // On a phone the list and conversation stack, so show one at a time:
  // picking a thread swaps the list out for the conversation.
  const narrow = useNarrowScreen();
  const showList = !(narrow && selected);
  const conversationRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (narrow && selected) conversationRef.current?.scrollIntoView({ block: 'start' });
  }, [narrow, selected]);

  return (
    <div>
      <h2 className="serif" style={{ fontSize: 28, fontWeight: 500, fontStyle: 'italic', margin: 0, marginBottom: 6 }}>
        Support
      </h2>
      <p style={{ color: 'var(--ink-3)', fontSize: 13, marginBottom: 20 }}>
        Inbound support email + in-app contact messages. Replies go back as threaded email from the support inbox.
      </p>

      {/* Status filter */}
      <div style={{ display: 'inline-flex', gap: 4, background: 'var(--surface-2)', padding: 4, borderRadius: 10, marginBottom: 18, maxWidth: '100%', overflowX: 'auto' }}>
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => { setFilter(f); if (narrow) setSelected(null); }}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              border: 'none',
              background: filter === f ? 'var(--surface)' : 'transparent',
              color: filter === f ? 'var(--ink)' : 'var(--ink-3)',
              fontSize: 13,
              fontWeight: filter === f ? 600 : 500,
              fontFamily: 'var(--font-ui)',
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16, alignItems: 'start' }}>
        {/* Threads list */}
        {showList && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            {loading ? (
              <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
            ) : !threads || threads.length === 0 ? (
              <div style={{ padding: 24, color: 'var(--ink-3)', fontSize: 13, textAlign: 'center', background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--line)' }}>
                No {filter === 'all' ? '' : filter} threads.
              </div>
            ) : (
              threads.map((t) => (
                <button
                  key={t._id}
                  type="button"
                  onClick={() => setSelected(t._id)}
                  style={{
                    textAlign: 'left',
                    background: selected === t._id ? 'var(--surface-2)' : 'var(--surface)',
                    border: '1px solid ' + (selected === t._id ? 'var(--line-strong)' : 'var(--line)'),
                    borderRadius: 12,
                    padding: 12,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.subject}
                    </span>
                    {t.unreadCount > 0 && (
                      <span style={{ flexShrink: 0, background: 'var(--coral)', color: '#fff', borderRadius: 100, fontSize: 10, fontWeight: 700, padding: '1px 7px' }}>
                        {t.unreadCount}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.fromName ? `${t.fromName} · ` : ''}{t.fromEmail}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="mono" style={{ fontSize: 10, letterSpacing: 0.6, textTransform: 'uppercase', color: STATUS_COLOR[t.status] }}>
                      {t.status}
                    </span>
                    <span className="mono" style={{ fontSize: 10, color: 'var(--ink-4)' }}>
                      {new Date(t.lastMessageAt).toLocaleDateString()}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {/* Conversation */}
        {selected ? (
          <div ref={conversationRef} style={{ minWidth: 0 }}>
            {narrow && (
              <button
                type="button"
                onClick={() => setSelected(null)}
                style={{ padding: '8px 0', marginBottom: 8, color: 'var(--coral-ink)', fontSize: 14, fontWeight: 600 }}
              >
                ← All threads
              </button>
            )}
            <ThreadView key={selected} token={token} threadId={selected} />
          </div>
        ) : (
          <div className="hide-mobile" style={{ padding: 40, color: 'var(--ink-3)', fontSize: 14, textAlign: 'center', background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--line)' }}>
            Select a thread to read and reply.
          </div>
        )}
      </div>
    </div>
  );
}

// Matches the shell's phone breakpoint in layout.css.
const NARROW_QUERY = '(max-width: 768px)';

function useNarrowScreen() {
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

function ThreadView({ token, threadId }: { token: string; threadId: Id<'supportThreads'> }) {
  const data = useQuery(api.admin.support.getThread, token ? { token, threadId } : 'skip');
  const markRead = useMutation(api.admin.support.markRead);
  const setStatus = useMutation(api.admin.support.setStatus);
  const setSpam = useMutation(api.admin.support.setSpam);
  const replyAction = useAction(api.admin.support.reply);
  const attachmentUrl = useAction(api.admin.support.attachmentUrl);

  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Mark read on open (once the thread loads).
  useEffect(() => {
    if (data) markRead({ token, threadId }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, data === undefined]);

  if (data === undefined) {
    return <div style={{ padding: 24, color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>;
  }
  if (data === null) {
    return <div style={{ padding: 24, color: 'var(--ink-3)', fontSize: 13 }}>Thread not found.</div>;
  }
  const { thread, messages } = data;

  const send = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    setErr(null);
    try {
      await replyAction({ token, threadId, body: body.trim() });
      setBody('');
    } catch (e) {
      setErr(friendlyError(e, 'Support'));
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status: Status) => {
    if (status === thread.status) return;
    setErr(null);
    try {
      await setStatus({ token, threadId, status });
    } catch (e) {
      setErr(friendlyError(e, 'Support'));
    }
  };

  const toggleSpam = async () => {
    setErr(null);
    try {
      await setSpam({ token, threadId, spam: !thread.spam });
    } catch (e) {
      setErr(friendlyError(e, 'Support'));
    }
  };

  const openAttachment = async (r2Key: string) => {
    try {
      const url = await attachmentUrl({ token, r2Key });
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setErr(friendlyError(e, 'Support'));
    }
  };

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, display: 'flex', flexDirection: 'column', minHeight: 400 }}>
      {/* Header */}
      <div className="wrap-mobile" style={{ padding: 16, borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {thread.spam && (
              <span className="mono" style={{ flexShrink: 0, fontSize: 9, letterSpacing: 0.6, textTransform: 'uppercase', color: '#fff', background: 'var(--coral)', borderRadius: 6, padding: '2px 6px' }}>
                spam
              </span>
            )}
            <div style={{ minWidth: 0, fontSize: 15, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {thread.subject}
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', overflowWrap: 'anywhere' }}>
            {thread.fromName ? `${thread.fromName} · ` : ''}{thread.fromEmail}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flexShrink: 0 }}>
          <button
            type="button"
            onClick={toggleSpam}
            title={thread.spam ? 'Move back to the inbox' : 'Move to the Spam tab'}
            style={{
              padding: '5px 10px',
              borderRadius: 8,
              border: '1px solid var(--line-strong)',
              background: 'transparent',
              color: 'var(--ink-3)',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: 'var(--font-ui)',
              cursor: 'pointer',
            }}
          >
            {thread.spam ? 'Not spam' : 'Mark spam'}
          </button>
          {(['open', 'pending', 'closed'] as Status[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => changeStatus(s)}
              style={{
                padding: '5px 10px',
                borderRadius: 8,
                border: '1px solid ' + (thread.status === s ? STATUS_COLOR[s] : 'var(--line-strong)'),
                background: thread.status === s ? STATUS_COLOR[s] : 'transparent',
                color: thread.status === s ? '#fff' : 'var(--ink-3)',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'var(--font-ui)',
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
        {messages.map((m) => {
          const outbound = m.direction === 'outbound';
          return (
            <div key={m._id} style={{ display: 'flex', justifyContent: outbound ? 'flex-end' : 'flex-start' }}>
              <div
                style={{
                  maxWidth: '78%',
                  background: m.system ? 'var(--surface-2)' : outbound ? 'var(--coral)' : 'var(--bg)',
                  color: outbound && !m.system ? '#fff' : 'var(--ink)',
                  border: m.system ? '1px dashed var(--line-strong)' : '1px solid var(--line)',
                  borderRadius: 12,
                  padding: '10px 12px',
                  fontSize: 14,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {m.system && (
                  <div className="mono" style={{ fontSize: 9, letterSpacing: 0.6, textTransform: 'uppercase', opacity: 0.7, marginBottom: 4 }}>
                    status update
                  </div>
                )}
                {m.body}
                {m.trimmed && (
                  <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>— quoted history hidden —</div>
                )}
                {m.attachments && m.attachments.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {m.attachments.map((a) => (
                      <button
                        key={a.r2Key}
                        type="button"
                        onClick={() => openAttachment(a.r2Key)}
                        style={{
                          textAlign: 'left',
                          background: 'transparent',
                          border: 'none',
                          color: outbound && !m.system ? '#fff' : 'var(--coral)',
                          textDecoration: 'underline',
                          fontSize: 12,
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        📎 {a.filename} ({Math.round(a.size / 1024)} KB)
                      </button>
                    ))}
                  </div>
                )}
                {m.spam && (
                  <div className="mono" style={{ fontSize: 9, letterSpacing: 0.4, opacity: 0.6, marginTop: 6 }} title="Jev spam classification (Workers AI)">
                    jev: {m.spam.verdict} {Math.round(m.spam.spamProbability * 100)}% · {m.spam.category.replace(/_/g, ' ')} · relevance {m.spam.appRelevance}/3
                  </div>
                )}
                <div style={{ fontSize: 10, opacity: 0.6, marginTop: 6 }}>
                  {new Date(m.createdAt).toLocaleString()}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {err && (
        <div style={{ margin: '0 16px', background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '8px 12px', borderRadius: 10, fontSize: 13 }}>
          {err}
        </div>
      )}

      {/* Reply composer */}
      <div style={{ padding: 16, borderTop: '1px solid var(--line)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={`Reply to ${thread.fromEmail}…`}
          rows={3}
          disabled={sending}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: 14,
            fontFamily: 'var(--font-ui)',
            background: 'var(--bg)',
            color: 'var(--ink)',
            resize: 'vertical',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={send}
            disabled={sending || !body.trim()}
            style={{
              padding: '9px 20px',
              background: 'var(--coral)',
              color: '#fff',
              border: 'none',
              borderRadius: 100,
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'var(--font-ui)',
              cursor: sending || !body.trim() ? 'default' : 'pointer',
              opacity: sending || !body.trim() ? 0.5 : 1,
            }}
          >
            {sending ? 'Sending…' : 'Send reply'}
          </button>
        </div>
      </div>
    </div>
  );
}
